import type { PrinterService, Ticket, ZReportTicket, KitchenTicket } from '../types';
import { EDITOR } from '@/config/editor';
import { formatPaymentLabel } from '@/services/print/payment-labels';

const SEP = '─'.repeat(32);
const DSEP = '═'.repeat(32);

/**
 * Sunmi inner printer service using the Sunmi SDK via Capacitor plugin.
 * Supports: D3 (80mm), D3 Mini 80mm (auto-cut), D3 Mini 58mm (tear-off)
 *
 * Full NF525-compliant receipt printing with TVA breakdown, fiscal hash,
 * payment details, customer info, Z reports, and kitchen orders.
 */
export class SunmiPrinter implements PrinterService {
  private initialized = false;
  private model: string = '';
  private hasAutoCut: boolean = false;

  async isAvailable(): Promise<boolean> {
    try {
      const { SunmiPlugin } = await import('../../../plugins/SunmiPlugin');
      const result = await SunmiPlugin.init();
      if (result.success) {
        const status = await SunmiPlugin.getStatus();
        this.initialized = true;
        this.model = status.model || '';
        this.hasAutoCut =
          this.model.includes('80') || this.model.includes('D3 PRO') || this.model.includes('D3-');
        return status.online;
      }
      return false;
    } catch {
      return false;
    }
  }

  private async getPlugin() {
    const { SunmiPlugin } = await import('../../../plugins/SunmiPlugin');
    if (!this.initialized) {
      await SunmiPlugin.init();
      this.initialized = true;
    }
    return SunmiPlugin;
  }

  // ─── Helpers ──────────────────────────────────────────────

  private async printLine(
    p: any,
    text: string,
    opts?: { bold?: boolean; size?: number; align?: 0 | 1 | 2 },
  ) {
    if (opts?.align !== undefined) await p.setAlign({ align: opts.align });
    if (opts?.bold !== undefined) await p.setBold({ bold: opts.bold });
    if (opts?.size !== undefined) await p.setFontSize({ size: opts.size });
    await p.printText({ text });
  }

  private padLine(left: string, right: string, width = 32): string {
    const gap = Math.max(1, width - left.length - right.length);
    return `${left}${' '.repeat(gap)}${right}`;
  }

  // ─── Full receipt (NF525 compliant) ───────────────────────

  async print(ticket: Ticket): Promise<void> {
    const p = await this.getPlugin();
    await p.beginTransaction();

    // ── Header: Shop info ──
    await this.printLine(p, ticket.shopName, { bold: true, size: 3, align: 1 });
    if (ticket.shopAddress)
      await this.printLine(p, ticket.shopAddress, { size: 1, align: 1, bold: false });
    if (ticket.shopPhone)
      await this.printLine(p, `Tél : ${ticket.shopPhone}`, { size: 1, align: 1 });
    if (ticket.shopEmail) await this.printLine(p, ticket.shopEmail, { size: 1, align: 1 });
    if (ticket.shopSiret)
      await this.printLine(p, `SIRET : ${ticket.shopSiret}`, { size: 1, align: 1 });

    await p.feedPaper({ lines: 1 });

    // ── Ticket meta ──
    if (ticket.registerName) await this.printLine(p, ticket.registerName, { size: 1, align: 1 });
    if (ticket.ticketNumber)
      await this.printLine(p, ticket.ticketNumber, { bold: true, size: 2, align: 1 });
    await this.printLine(
      p,
      `${ticket.date.toLocaleDateString('fr-FR')} ${ticket.date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`,
      { size: 1, align: 1 },
    );
    if (ticket.cashierName)
      await this.printLine(p, `Vendeur : ${ticket.cashierName}`, { size: 1, align: 1 });

    // ── Customer ──
    if (ticket.customerName) {
      await this.printLine(p, SEP, { align: 0, size: 1, bold: false });
      await this.printLine(p, `Client : ${ticket.customerName}`, { size: 1 });
      if (ticket.customerPhone)
        await this.printLine(p, `Tél : ${ticket.customerPhone}`, { size: 1 });
      if (ticket.customerEmail) await this.printLine(p, ticket.customerEmail, { size: 1 });
    }

    // ── Items ──
    await this.printLine(p, SEP, { align: 0, size: 1, bold: false });
    await p.setFontSize({ size: 2 });
    for (const line of ticket.lines) {
      const lineTotal = (line.qty * line.price).toFixed(2);
      const label = `${line.qty}x ${line.label}`;
      await p.printText({ text: this.padLine(label, `${lineTotal} €`) });
    }

    // ── Discount ──
    if (ticket.discountPercent && ticket.discountPercent > 0 && ticket.subtotal) {
      await this.printLine(p, SEP, { align: 0 });
      const discountAmount = ((ticket.subtotal * ticket.discountPercent) / 100).toFixed(2);
      await p.printText({
        text: this.padLine(`Remise ${ticket.discountPercent}%`, `-${discountAmount} €`),
      });
    }

    // ── TVA breakdown ──
    if (ticket.tvaBreakdown && ticket.tvaBreakdown.length > 0) {
      await this.printLine(p, SEP, { align: 0, size: 1 });
      const totalHT = ticket.tvaBreakdown.reduce((s, t) => s + t.ht, 0);
      const totalTVA = ticket.tvaBreakdown.reduce((s, t) => s + t.tva, 0);
      await p.printText({ text: this.padLine('Sous-total HT', `${totalHT.toFixed(2)} €`) });
      for (const t of ticket.tvaBreakdown) {
        await p.printText({ text: this.padLine(`TVA ${t.rate}%`, `${t.tva.toFixed(2)} €`) });
      }
      await p.printText({ text: this.padLine('Total TVA', `${totalTVA.toFixed(2)} €`) });
    }

    // ── TOTAL ──
    await this.printLine(p, DSEP, { align: 0 });
    await this.printLine(p, this.padLine('TOTAL TTC', `${ticket.total.toFixed(2)} €`), {
      bold: true,
      size: 3,
      align: 0,
    });
    await this.printLine(p, DSEP, { size: 1, bold: false });

    // ── Payment ──
    if (ticket.splitPayments && ticket.splitPayments.length > 0) {
      for (const sp of ticket.splitPayments) {
        await p.printText({
          text: this.padLine(formatPaymentLabel(sp.method), `${sp.amount.toFixed(2)} €`),
        });
      }
    } else if (ticket.paymentMethod) {
      await p.printText({
        text: this.padLine('Règlement', formatPaymentLabel(ticket.paymentMethod)),
      });
    }

    if (ticket.changeGiven && ticket.changeGiven > 0) {
      await p.setBold({ bold: true });
      await p.printText({
        text: this.padLine('Monnaie rendue', `${ticket.changeGiven.toFixed(2)} €`),
      });
      await p.setBold({ bold: false });
    }

    // ── Fiscal footer (NF525) ──
    await p.feedPaper({ lines: 1 });
    await p.setAlign({ align: 1 });
    await p.setFontSize({ size: 1 });
    if (ticket.fiscalHash) {
      await p.printText({ text: `SHA-256 : ${ticket.fiscalHash.substring(0, 16)}...` });
    }
    await p.setBold({ bold: true });
    await p.printText({ text: EDITOR.nf525Mention });
    await p.setBold({ bold: false });
    await p.printText({ text: `${EDITOR.softwareName} — www.visionpos.fr` });
    await p.printText({ text: `Éditeur : ${EDITOR.legalName}` });
    await p.feedPaper({ lines: 1 });
    await p.printText({ text: 'Merci de votre visite !' });
    await p.feedPaper({ lines: 3 });

    if (this.hasAutoCut) await p.cutPaper();
    await p.commitTransaction();
  }

  // ─── HTML printing (pass-through to Sunmi native) ─────────

  async printHtml(html: string): Promise<void> {
    const p = await this.getPlugin();
    try {
      await p.printHtml({ html });
      await p.feedPaper({ lines: 3 });
      if (this.hasAutoCut) await p.cutPaper();
    } catch {
      // Fallback: strip HTML to text and print via ESC/POS
      const div = document.createElement('div');
      div.innerHTML = html;
      const text = div.textContent || div.innerText || '';
      await p.beginTransaction();
      await p.setFontSize({ size: 2 });
      await p.setAlign({ align: 0 });
      const lines = text.split('\n').filter((l) => l.trim());
      for (const line of lines) {
        await p.printText({ text: line.trim() });
      }
      await p.feedPaper({ lines: 3 });
      if (this.hasAutoCut) await p.cutPaper();
      await p.commitTransaction();
    }
  }

  // ─── Z Report (NF525 fiscal closure) ──────────────────────

  async printZReport(report: ZReportTicket): Promise<void> {
    const p = await this.getPlugin();
    await p.beginTransaction();

    // Header
    await this.printLine(p, report.storeName, { bold: true, size: 3, align: 1 });
    if (report.storeSiret)
      await this.printLine(p, `SIRET : ${report.storeSiret}`, { size: 1, align: 1, bold: false });
    await p.feedPaper({ lines: 1 });

    await this.printLine(p, 'RAPPORT Z — CLÔTURE FISCALE', { bold: true, size: 2, align: 1 });
    await this.printLine(p, `N° ${report.reportNumber}`, { bold: true, size: 2, align: 1 });
    await p.feedPaper({ lines: 1 });

    // Period
    await p.setAlign({ align: 0 });
    await p.setFontSize({ size: 1 });
    await p.setBold({ bold: false });
    await p.printText({ text: this.padLine('Date', report.date) });
    await p.printText({
      text: this.padLine('Période', `${report.periodStart} — ${report.periodEnd}`),
    });
    if (report.cashierName)
      await p.printText({ text: this.padLine('Caissier', report.cashierName) });
    await p.printText({ text: this.padLine('Nb tickets', String(report.ticketCount)) });

    // Totals
    await this.printLine(p, SEP, { align: 0 });
    await p.setFontSize({ size: 2 });
    await p.printText({ text: this.padLine('Total HT', `${report.totalHT.toFixed(2)} €`) });
    await p.printText({ text: this.padLine('Total TVA', `${report.totalTVA.toFixed(2)} €`) });
    await p.setBold({ bold: true });
    await p.printText({ text: this.padLine('TOTAL TTC', `${report.totalTTC.toFixed(2)} €`) });
    await p.setBold({ bold: false });

    // TVA detail
    if (report.tvaDetails.length > 0) {
      await this.printLine(p, SEP, { align: 0, size: 1 });
      await this.printLine(p, 'DÉTAIL TVA', { bold: true, size: 1, align: 1 });
      await p.setAlign({ align: 0 });
      await p.setBold({ bold: false });
      for (const t of report.tvaDetails) {
        await p.printText({
          text: this.padLine(
            `TVA ${t.rate}%`,
            `HT ${t.base.toFixed(2)} | TVA ${t.amount.toFixed(2)}`,
          ),
        });
      }
    }

    // Payment breakdown
    if (report.paymentBreakdown.length > 0) {
      await this.printLine(p, SEP, { align: 0, size: 1 });
      await this.printLine(p, 'VENTILATION PAIEMENTS', { bold: true, size: 1, align: 1 });
      await p.setAlign({ align: 0 });
      await p.setBold({ bold: false });
      for (const pm of report.paymentBreakdown) {
        const label = PAYMENT_LABELS[pm.method] || pm.method;
        await p.printText({
          text: this.padLine(`${label} (×${pm.count})`, `${pm.total.toFixed(2)} €`),
        });
      }
    }

    // Fiscal footer
    await this.printLine(p, DSEP, { align: 0, size: 1 });
    await p.setAlign({ align: 1 });
    await p.printText({ text: `Grand total séq. : ${report.grandTotalSequence}` });
    await p.printText({ text: `SHA-256 : ${report.fiscalHash.substring(0, 20)}...` });
    await p.setBold({ bold: true });
    await p.printText({ text: EDITOR.nf525Mention });
    await p.setBold({ bold: false });
    await p.printText({ text: `${EDITOR.softwareName} — www.visionpos.fr` });
    await p.feedPaper({ lines: 4 });

    if (this.hasAutoCut) await p.cutPaper();
    await p.commitTransaction();
  }

  // ─── Kitchen order (KDS) ──────────────────────────────────

  async printKitchenOrder(order: KitchenTicket): Promise<void> {
    const p = await this.getPlugin();
    await p.beginTransaction();

    // Big header for kitchen readability
    await this.printLine(p, '*** COMMANDE CUISINE ***', { bold: true, size: 3, align: 1 });
    await p.feedPaper({ lines: 1 });
    await this.printLine(p, `N° ${order.orderNumber}`, { bold: true, size: 3, align: 1 });
    await this.printLine(p, order.destination.toUpperCase(), { bold: true, size: 2, align: 1 });

    if (order.tableNumber) {
      await this.printLine(p, `TABLE ${order.tableNumber}`, { bold: true, size: 3, align: 1 });
    }

    await p.feedPaper({ lines: 1 });
    await this.printLine(p, SEP, { align: 0, size: 1, bold: false });

    // Time + server
    const timeStr = order.time.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    await p.setFontSize({ size: 1 });
    await p.printText({ text: this.padLine('Heure', timeStr) });
    if (order.serverName) await p.printText({ text: this.padLine('Serveur', order.serverName) });

    await this.printLine(p, SEP, { align: 0 });

    // Items — large font for kitchen
    await p.setFontSize({ size: 3 });
    await p.setBold({ bold: true });
    for (const item of order.items) {
      await p.printText({ text: `${item.quantity}x ${item.name}` });
      if (item.notes) {
        await p.setFontSize({ size: 2 });
        await p.setBold({ bold: false });
        await p.printText({ text: `   >> ${item.notes}` });
        await p.setFontSize({ size: 3 });
        await p.setBold({ bold: true });
      }
    }

    // Global notes
    if (order.notes) {
      await this.printLine(p, SEP, { align: 0, size: 1, bold: false });
      await this.printLine(p, `NOTE: ${order.notes}`, { bold: true, size: 2, align: 0 });
    }

    await p.feedPaper({ lines: 4 });
    if (this.hasAutoCut) await p.cutPaper();
    await p.commitTransaction();
  }

  // ─── Cash drawer ──────────────────────────────────────────

  async openCashDrawer(): Promise<void> {
    const p = await this.getPlugin();
    await p.openCashDrawer();
  }

  /** Show amount on customer-facing display */
  async setCustomerDisplay(text: string, amount?: number): Promise<void> {
    const p = await this.getPlugin();
    await p.setCustomerDisplay({ text, amount });
  }
}
